import React from 'react';
import styled from 'styled-components';
import { CheckCircle2, Wrench, Ban, Info, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { CONFIDENCE_LABELS, scoreTone } from './labels';

const TONE_COLORS = {
    good: colors.successDark,
    fair: colors.goldDark,
    weak: colors.dangerDark,
};

export function Section({ icon: IconComponent, title, subtitle, aside, children }) {
    return (
        <SectionWrap>
            <SectionHead>
                <SectionTitleRow>
                    {IconComponent && (
                        <SectionIcon>
                            <IconComponent size={16} color={panelAccent(colors.secondaryPurple)} />
                        </SectionIcon>
                    )}
                    <div>
                        <SectionTitle>{title}</SectionTitle>
                        {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
                    </div>
                </SectionTitleRow>
                {aside}
            </SectionHead>
            {children}
        </SectionWrap>
    );
}

/** Nota de 0 a 100 num anel, na cor do painel. */
export function ScoreRing({ score, size = 132, label }) {
    const stroke = 10;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);

    return (
        <RingWrap style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden='true'>
                <circle cx={size / 2} cy={size / 2} r={radius} fill='none' stroke={colors.neutralDark} strokeWidth={stroke} />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill='none'
                    stroke={panelAccent(colors.secondaryPurple)}
                    strokeWidth={stroke}
                    strokeLinecap='round'
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <RingLabel>
                <RingScore>{score}</RingScore>
                {label && <RingCaption>{label}</RingCaption>}
            </RingLabel>
        </RingWrap>
    );
}

/** Variação em relação à análise anterior: só aparece quando existe uma. */
export function Delta({ current, previous }) {
    if (previous === null || previous === undefined) {
        return null;
    }

    const diff = current - previous;

    if (diff === 0) {
        return <DeltaText $tone='neutral'>= {previous}</DeltaText>;
    }

    const Arrow = diff > 0 ? ArrowUpRight : ArrowDownRight;

    return (
        <DeltaText $tone={diff > 0 ? 'up' : 'down'} title={`Antes: ${previous}`}>
            <Arrow size={13} /> {diff > 0 ? '+' : ''}{diff}
        </DeltaText>
    );
}

export function ScoreBar({ title, score, previous }) {
    const tone = scoreTone(score);

    return (
        <BarRow>
            <BarHead>
                <BarTitle>{title}</BarTitle>
                <BarScore>
                    <Delta current={score} previous={previous} />
                    <strong style={{ color: TONE_COLORS[tone] }}>{score}</strong>
                </BarScore>
            </BarHead>
            <BarTrack>
                <BarFill style={{ width: `${score}%`, background: TONE_COLORS[tone] }} />
            </BarTrack>
        </BarRow>
    );
}

const CHECKLIST_GROUPS = [
    { key: 'keep', label: 'O que já funciona', icon: CheckCircle2, color: colors.successDark },
    { key: 'adjust', label: 'O que ajustar', icon: Wrench, color: colors.goldDark },
    { key: 'stop', label: 'O que evitar', icon: Ban, color: colors.dangerDark },
];

/**
 * Um pilar com nota: veredito, aviso de confiança e a lista de pontos. Os
 * três grupos (`keep`, `adjust`, `stop`) viram uma lista única, cada item com
 * o próprio ícone, em vez de colunas lado a lado.
 */
export function PillarBlock({ pillar, previousScore }) {
    const tone = scoreTone(pillar.score);

    return (
        <PillarCard>
            <PillarTop>
                <div>
                    <PillarTitle>{pillar.title}</PillarTitle>
                    <PillarVerdict>{pillar.verdict}</PillarVerdict>
                </div>
                <PillarScore>
                    <span style={{ color: TONE_COLORS[tone] }}>{pillar.score}</span>
                    <small>/100</small>
                    <Delta current={pillar.score} previous={previousScore} />
                </PillarScore>
            </PillarTop>

            {pillar.confidence !== 'high' && (
                <Notice>
                    <Info size={14} />
                    <span>
                        <strong>{CONFIDENCE_LABELS[pillar.confidence]}.</strong> {pillar.note}
                    </span>
                </Notice>
            )}

            <Checklist>
                {CHECKLIST_GROUPS.map(group => (pillar[group.key] || []).length > 0 && (
                    <ChecklistGroup key={group.key}>
                        <GroupLabel style={{ color: group.color }}>
                            <group.icon size={14} /> {group.label}
                        </GroupLabel>
                        {pillar[group.key].map((item, index) => (
                            <ChecklistItem key={index} style={{ borderLeftColor: group.color }}>
                                {item.lead && <strong>{item.lead} </strong>}
                                {item.text}
                            </ChecklistItem>
                        ))}
                    </ChecklistGroup>
                ))}
            </Checklist>
        </PillarCard>
    );
}

export const Card = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}1A;
    border-radius: 12px;
    padding: 18px;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

export const Grid = styled.div`
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(auto-fill, minmax(${({ $min }) => $min || '240px'}, 1fr));
`;

export const Muted = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: ${colors.night};
`;

export const Label = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightLight};
    margin-bottom: 4px;
`;

export const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 500;
    background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
    color: ${panelAccent(colors.secondaryPurple)};
    white-space: nowrap;
`;

const SectionWrap = styled.section`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 32px;
`;

const SectionHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const SectionTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SectionIcon = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    flex-shrink: 0;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SectionSubtitle = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const RingWrap = styled.div`
    position: relative;
    flex-shrink: 0;
`;

const RingLabel = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const RingScore = styled.span`
    font-size: 36px;
    font-weight: 700;
    color: ${colors.nightDark};
    line-height: 1;
`;

const RingCaption = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    margin-top: 4px;
`;

const DeltaText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 1px;
    font-size: 12px;
    font-weight: 600;
    color: ${({ $tone }) => ({ up: colors.successDark, down: colors.dangerDark, neutral: colors.nightLight })[$tone]};
`;

const BarRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BarHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 14px;
`;

const BarTitle = styled.span`
    color: ${colors.nightDark};
`;

const BarScore = styled.span`
    display: inline-flex;
    gap: 8px;
    align-items: baseline;
`;

const BarTrack = styled.div`
    height: 8px;
    border-radius: 8px;
    background: ${colors.neutralDark};
    overflow: hidden;
`;

const BarFill = styled.div`
    height: 100%;
    border-radius: 8px;
    transition: width 0.6s ease;
`;

const PillarCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const PillarTop = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
`;

const PillarTitle = styled.h4`
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const PillarVerdict = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.night};
`;

const PillarScore = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;

    span:first-child {
        font-size: 28px;
        font-weight: 700;
        line-height: 1;
    }

    small {
        font-size: 11px;
        color: ${colors.nightLight};
    }
`;

const Notice = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${colors.neutralBackground};
    color: ${colors.night};
    font-size: 13px;
    line-height: 1.45;

    svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: ${colors.primary};
    }
`;

const Checklist = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const ChecklistGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const GroupLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
`;

const ChecklistItem = styled.p`
    margin: 0;
    padding: 2px 0 2px 12px;
    border-left: 2px solid;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.night};

    strong {
        color: ${colors.nightDark};
        font-weight: 600;
    }
`;
